/**
 * Business Profile Hooks
 *
 * React Query hooks for the user's business profile (1:1 relationship).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { businessApi } from '@/services/api/businessApi';
import {
  BusinessProfileSetupRequest,
  BusinessProfileUpdateRequest,
} from '@/types/business';

// Query keys
export const businessKeys = {
  all: ['business-profile'] as const,
  profile: () => [...businessKeys.all, 'current'] as const,
  status: () => [...businessKeys.all, 'status'] as const,
};

/**
 * Get the current user's business profile.
 * Returns null if not setup (redirect to onboarding).
 */
export function useBusinessProfile() {
  return useQuery({
    queryKey: businessKeys.profile(),
    queryFn: () => businessApi.getProfile(),
    staleTime: 5 * 60 * 1000, // 5 minutes - profile rarely changes
  });
}

/**
 * Check if business profile is complete.
 * Used to determine if user needs onboarding.
 */
export function useBusinessStatus() {
  return useQuery({
    queryKey: businessKeys.status(),
    queryFn: () => businessApi.isSetupComplete(),
  });
}

/**
 * Setup business profile (onboarding).
 */
export function useSetupBusinessProfile() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (data: BusinessProfileSetupRequest) =>
      businessApi.setupProfile(data),
    onSuccess: profile => {
      // Update cache
      queryClient.setQueryData(businessKeys.profile(), profile);
      queryClient.setQueryData(businessKeys.status(), true);

      // Navigate to manual entry (correct route path)
      navigate('/nib-c10/manual-entry');
    },
  });
}

/**
 * Update business profile.
 */
export function useUpdateBusinessProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: BusinessProfileUpdateRequest) =>
      businessApi.updateProfile(data),
    onSuccess: profile => {
      queryClient.setQueryData(businessKeys.profile(), profile);
    },
  });
}

/**
 * Hook to check if user needs onboarding.
 * Redirects to onboarding if profile not complete.
 */
export function useRequireBusinessProfile() {
  const navigate = useNavigate();
  const { data: profile, isLoading } = useBusinessProfile();

  if (!isLoading && !profile) {
    navigate('/onboarding');
  }

  return { profile, isLoading, isSetup: !!profile };
}
